import type {PrintoutBlockTextStyle} from "../shared/blockAttributes";
import {
    parseEnumAttribute,
    parseTextAttribute,
    parseTextStyleAttribute,
} from "../shared/blockAttributes";

// TODO Replace the billing-rollup string heuristic with a backend isRollup flag.
export const FOLIO_ROLLUP_DESCRIPTION_SUFFIX = " - Category total";
export const FOLIO_ROLLUP_DESCRIPTION_PREFIX = "[Fixed charge]";

export const isSyntheticFolioItem = (item: Record<string, unknown>) => {
    const description = typeof item.description === "string" ? item.description.trim() : "";
    if (!description) return false;

    return description.endsWith(FOLIO_ROLLUP_DESCRIPTION_SUFFIX)
        || description.startsWith(FOLIO_ROLLUP_DESCRIPTION_PREFIX);
};

export type FolioAlign = "left" | "center" | "right";
export type FolioSortDirection = "asc" | "desc";

export type FolioTextStyle = PrintoutBlockTextStyle;

export const FOLIO_ALIGNS: FolioAlign[] = ["left", "center", "right"];

export interface FolioColumn {
    id: string;
    variableKey: string;
    label: string;
    align: FolioAlign;
    headerFormat: FolioTextStyle;
    cellFormat: FolioTextStyle;
}

export interface FolioTotal {
    id: string;
    variableKey: string;
    label: string;
    format: FolioTextStyle;
    hideWhenUnresolved?: boolean;
}

export interface FolioSort {
    // null means chronological item-date order.
    columnId: string | null;
    direction: FolioSortDirection;
}

export interface PrintoutFolioTableAttrs {
    columns: FolioColumn[];
    sort: FolioSort;
    totals: FolioTotal[];
    excludeSyntheticRows: boolean;
    groupByDate: boolean;
}

// Preview-only resolved data; never serialized.
export interface FolioResolved {
    items: Record<string, string>[];
    totals: Record<string, string>;
    // null renders flat; empty group labels represent undated trailing rows.
    groups?: FolioResolvedSection[] | null;
    inclusiveRowIndexes?: number[] | null;
    hiddenColumnIds?: string[] | null;
    hiddenTotalIds?: string[] | null;
}

export interface FolioResolvedSection {
    label: string;
    startIndex: number;
    rowCount: number;
}

export const FOLIO_TAX_COLUMN_KEYS = new Set(["folioItemTaxRate", "folioItemTotalBeforeTax"]);

// Sort rendered date columns by their raw timestamps.
export const FOLIO_ITEM_DATE_FIELDS: Record<string, "timeSpanStart" | "timeSpanEnd"> = {
    folioItemStartDate: "timeSpanStart",
    folioItemStartTime: "timeSpanStart",
    folioItemEndDate: "timeSpanEnd",
    folioItemEndTime: "timeSpanEnd",
};

// Net and VAT totals print only as a resolved pair.
export const FOLIO_NET_TOTAL_KEY = "folioTotalBeforeTax";
export const FOLIO_TAX_TOTAL_KEY = "folioTaxTotal";

export const FOLIO_INCLUSIVE_LABEL = "(Inclusive)";

export const FOLIO_COLUMN_VARIABLES = [
    {key: "folioItemDescription", label: "Description"},
    {key: "folioItemPriceDescription", label: "Price description"},
    {key: "folioItemQuantity", label: "Quantity"},
    {key: "folioItemPrice", label: "Unit price"},
    {key: "folioItemTotal", label: "Amount"},
    {key: "folioItemTotalBeforeTax", label: "Amount before tax"},
    {key: "folioItemDiscount", label: "Discount"},
    {key: "folioItemCategory", label: "Category"},
    {key: "folioItemTaxRate", label: "Tax rate"},
    {key: "folioItemStartDate", label: "Start date"},
    {key: "folioItemStartTime", label: "Start time"},
    {key: "folioItemEndDate", label: "End date"},
    {key: "folioItemEndTime", label: "End time"},
    {key: "folioItemDuration", label: "Duration"},
    {key: "folioItemCode", label: "Code"},
    {key: "folioItemInclusive", label: "Inclusive"},
] as const;

export const FOLIO_TOTAL_VARIABLES = [
    // Reservation-scoped, not the whole folio.
    {key: "folioReservationTotalBeforeTax", label: "Reservation charges before tax"},
    {key: "folioReservationTotal", label: "Reservation charges"},
    {key: "folioFixedChargesTotal", label: "Additional charges"},
    {key: "folioTotalBeforeTax", label: "Total before tax"},
    {key: "folioTotal", label: "Total"},
    {key: "folioAmountPaid", label: "Paid"},
    {key: "folioAmountDue", label: "Balance due"},
    {key: "folioAmountRemaining", label: "Remaining"},
] as const;

export const getFolioColumnVariableLabel = (variableKey: string) => (
    FOLIO_COLUMN_VARIABLES.find(variable => variable.key === variableKey)?.label ?? variableKey
);

const RIGHT_ALIGN_KEYS = new Set([
    "folioItemPrice",
    "folioItemTotal",
    "folioItemTotalBeforeTax",
    "folioItemDiscount",
    "folioItemTaxRate",
]);
const CENTER_ALIGN_KEYS = new Set(["folioItemQuantity"]);

export const getFolioDefaultAlign = (variableKey: string): FolioAlign => {
    if (RIGHT_ALIGN_KEYS.has(variableKey)) return "right";
    if (CENTER_ALIGN_KEYS.has(variableKey)) return "center";

    return "left";
};

export const FOLIO_PROTECTED_TOTAL_KEY = "folioTotal";

export const getFolioTotalVariableLabel = (variableKey: string) => (
    FOLIO_TOTAL_VARIABLES.find(variable => variable.key === variableKey)?.label ?? variableKey
);

export const createFolioId = () => Math.random().toString(36).slice(2, 10);

const normalizeColumn = (column: Partial<FolioColumn> | null | undefined): FolioColumn => {
    const variableKey = parseTextAttribute(column?.variableKey, "folioItemDescription");

    return {
        align: parseEnumAttribute(column?.align, FOLIO_ALIGNS, "left"),
        cellFormat: parseTextStyleAttribute(column?.cellFormat),
        headerFormat: parseTextStyleAttribute(column?.headerFormat),
        id: parseTextAttribute(column?.id, createFolioId()),
        label: parseTextAttribute(column?.label, getFolioColumnVariableLabel(variableKey)),
        variableKey,
    };
};

const normalizeTotal = (total: Partial<FolioTotal> | null | undefined): FolioTotal => {
    const variableKey = parseTextAttribute(total?.variableKey, "folioTotal");

    return {
        format: parseTextStyleAttribute(total?.format),
        // The grand total must always remain visible.
        hideWhenUnresolved: variableKey !== FOLIO_PROTECTED_TOTAL_KEY && total?.hideWhenUnresolved === true,
        id: parseTextAttribute(total?.id, createFolioId()),
        label: parseTextAttribute(total?.label, getFolioTotalVariableLabel(variableKey)),
        variableKey,
    };
};

export const createFolioTotal = (variableKey: string, patch?: Partial<FolioTotal>): FolioTotal => ({
    format: {bold: false, italic: false},
    hideWhenUnresolved: true,
    id: createFolioId(),
    label: getFolioTotalVariableLabel(variableKey),
    variableKey,
    ...patch,
});

export const DEFAULT_FOLIO_COLUMNS: FolioColumn[] = [
    {align: "left", cellFormat: {bold: false, italic: false}, headerFormat: {bold: false, italic: false}, id: "description", label: "Description", variableKey: "folioItemDescription"},
    {align: "center", cellFormat: {bold: false, italic: false}, headerFormat: {bold: false, italic: false}, id: "quantity", label: "Quantity", variableKey: "folioItemQuantity"},
    {align: "right", cellFormat: {bold: false, italic: false}, headerFormat: {bold: false, italic: false}, id: "price", label: "Unit price", variableKey: "folioItemPrice"},
    {align: "right", cellFormat: {bold: true, italic: false}, headerFormat: {bold: false, italic: false}, id: "amount", label: "Amount", variableKey: "folioItemTotal"},
];

// Net/VAT are opt-in because most folios do not record before-tax totals.
export const DEFAULT_FOLIO_TOTALS: FolioTotal[] = [
    {format: {bold: true, italic: false}, hideWhenUnresolved: false, id: "total", label: "Total", variableKey: "folioTotal"},
    {format: {bold: false, italic: false}, hideWhenUnresolved: true, id: "paid", label: "Paid", variableKey: "folioAmountPaid"},
    {format: {bold: false, italic: false}, hideWhenUnresolved: true, id: "due", label: "Balance due", variableKey: "folioAmountDue"},
];

export const DEFAULT_FOLIO_ATTRS: PrintoutFolioTableAttrs = {
    columns: DEFAULT_FOLIO_COLUMNS,
    excludeSyntheticRows: true,
    groupByDate: false,
    sort: {columnId: null, direction: "asc"},
    totals: DEFAULT_FOLIO_TOTALS,
};

export const getFolioTableAttrs = (attrs?: Partial<PrintoutFolioTableAttrs> | null): PrintoutFolioTableAttrs => {
    const columns = Array.isArray(attrs?.columns) && attrs!.columns.length
        ? attrs!.columns.map(normalizeColumn)
        : DEFAULT_FOLIO_COLUMNS;
    const totals = Array.isArray(attrs?.totals) ? attrs!.totals.map(normalizeTotal) : DEFAULT_FOLIO_TOTALS;
    const sortColumnId = attrs?.sort?.columnId ?? null;
    // Removed sort columns fall back to chronological order.
    const resolvedSortColumnId = columns.some(column => column.id === sortColumnId) ? sortColumnId : null;

    return {
        columns,
        excludeSyntheticRows: attrs?.excludeSyntheticRows !== false,
        groupByDate: attrs?.groupByDate === true,
        sort: {
            columnId: resolvedSortColumnId,
            direction: parseEnumAttribute(attrs?.sort?.direction, ["asc", "desc"], "asc"),
        },
        totals,
    };
};
